import React, { useEffect } from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerHeader,
  DrawerOverlay,
  Button,
  Input,
  Select,
  useToast,
  VStack,
  HStack,
  SimpleGrid,
} from '@chakra-ui/react';
import AdminDrawerContent from '../layout/AdminDrawerContent';
import { AdminField, useAdminFormFieldProps } from '../layout/AdminFormControls';
import { getData } from '../../utils/httpClient';

const ProvidersEditModal = ({
  isOpen,
  onClose,
  provider,
  isNew,
  onCreate,
  onUpdate,
}) => {
  const [formData, setFormData] = React.useState({});
  const [countryList, setCountryList] = React.useState([]);
  const [departmentsList, setDepartmentsList] = React.useState([]);
  const [localitiesList, setLocalitiesList] = React.useState([]);
  const toast = useToast();
  const { fieldProps, selectProps } = useAdminFormFieldProps();

  const getDepartments = async (countryId = null) => {
    try {
      const url = countryId ? `/departments/country/${countryId}` : '/departments';
      const response = await getData(url);
      setDepartmentsList(response?.payload?.departments || []);
    } catch (error) {
      toast({
        title: 'Error al obtener departamentos.',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getLocalities = async (departmentId = null) => {
    try {
      const url = departmentId ? `/localities/department/${departmentId}` : '/localities';
      const response = await getData(url);
      setLocalitiesList(response?.payload?.localities || []);
    } catch (error) {
      toast({
        title: 'Error al obtener localidades.',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const getCountries = async () => {
    try {
      const response = await getData('/countries');
      setCountryList(response?.payload?.countries || []);
    } catch (error) {
      toast({
        title: 'Error al obtener los paises.',
        description: error.message || 'Ocurrió un error inesperado.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    getCountries();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const uyId =
      provider?.countryId ||
      countryList.find((c) => c.name?.toUpperCase() === 'URUGUAY')?.id ||
      0;

    setFormData({
      id: provider?.id ?? null,
      name: provider?.name ?? '',
      phone: provider?.phone ?? '',
      email: provider?.email ?? '',
      address: provider?.address ?? '',
      rut: provider?.rut ?? '',
      localityId: provider?.localityId ?? 0,
      departmentId: provider?.locality?.departmentId ?? 0,
      countryId: uyId,
    });

    if (uyId) getDepartments(uyId);
    if (provider?.locality?.departmentId) {
      getLocalities(provider.locality.departmentId);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, isNew, isOpen, countryList]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const formattedValue =
      name === 'localityId' || name === 'countryId' || name === 'departmentId'
        ? value === ''
          ? 0
          : Number(value)
        : value;

    setFormData((prev) => ({ ...prev, [name]: formattedValue }));

    if (name === 'countryId' && value !== '0') {
      getDepartments(value);
      setFormData((prev) => ({ ...prev, countryId: Number(value), departmentId: 0, localityId: 0 }));
      setLocalitiesList([]);
    }
    if (name === 'departmentId' && value !== '0') {
      getLocalities(value);
      setFormData((prev) => ({ ...prev, departmentId: Number(value), localityId: 0 }));
    }
  };

  const handleSave = () => {
    const providerData = {
      id: formData.id,
      name: formData.name,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      rut: formData.rut,
      localityId: formData.localityId === 0 ? null : formData.localityId,
      countryId: formData.countryId === 0 ? null : formData.countryId,
    };

    if (isNew) {
      onCreate(providerData);
    } else {
      onUpdate(providerData);
    }
    toast({
      title: isNew ? 'Proveedor creado.' : 'Proveedor actualizado.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
    onClose();
  };

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={onClose} closeOnOverlayClick={false}>
      <DrawerOverlay />
      <AdminDrawerContent>
        <DrawerCloseButton />
        <DrawerHeader fontSize="lg" fontWeight="bold">
          {isNew ? 'Nuevo proveedor' : 'Editar proveedor'}
        </DrawerHeader>
        <DrawerBody pb={6}>
          <VStack spacing={4} align="stretch">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <AdminField label="Nombre" isRequired>
                <Input name="name" value={formData.name} onChange={handleChange} {...fieldProps} />
              </AdminField>
              <AdminField label="RUT">
                <Input name="rut" value={formData.rut} onChange={handleChange} {...fieldProps} />
              </AdminField>
              <AdminField label="Dirección">
                <Input name="address" value={formData.address} onChange={handleChange} {...fieldProps} />
              </AdminField>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
              <AdminField label="País">
                <Select name="countryId" value={formData.countryId || 0} onChange={handleChange} {...selectProps}>
                  <option value={0}>Seleccionar país</option>
                  {countryList.map((country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </Select>
              </AdminField>
              <AdminField label="Departamento">
                <Select
                  name="departmentId"
                  value={formData.departmentId || 0}
                  onChange={handleChange}
                  isDisabled={!formData.countryId}
                  {...selectProps}
                >
                  <option value={0}>Seleccionar departamento</option>
                  {departmentsList.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </Select>
              </AdminField>
              <AdminField label="Localidad">
                <Select
                  name="localityId"
                  value={formData.localityId || 0}
                  onChange={handleChange}
                  isDisabled={!formData.departmentId}
                  {...selectProps}
                >
                  <option value={0}>Seleccionar localidad</option>
                  {localitiesList.map((locality) => (
                    <option key={locality.id} value={locality.id}>
                      {locality.name}
                    </option>
                  ))}
                </Select>
              </AdminField>
            </SimpleGrid>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <AdminField label="Teléfono">
                <Input name="phone" value={formData.phone} onChange={handleChange} {...fieldProps} />
              </AdminField>
              <AdminField label="Mail">
                <Input name="email" type="email" value={formData.email} onChange={handleChange} {...fieldProps} />
              </AdminField>
            </SimpleGrid>
          </VStack>
        </DrawerBody>
        <HStack justify="flex-end" p={4} borderTopWidth="1px" borderColor="gray.100">
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button size="sm" variant="adminPrimary" onClick={handleSave}>
            Guardar
          </Button>
        </HStack>
      </AdminDrawerContent>
    </Drawer>
  );
};

export default ProvidersEditModal;
